/**
 * Subnet Range Configuration Component
 *
 * Formulario para configurar ranges DHCP e estatico de um subnet.
 * Usado na aba IPAM do NetworkingPage.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  SaveIcon,
  Loader2Icon,
  AlertTriangleIcon,
  CheckCircleIcon,
  NetworkIcon,
  RadioIcon,
  ServerIcon,
  InfoIcon,
} from 'lucide-react'
import { useState } from 'react'

import { endCustomerIpamApi } from '@nebula/shared/services'
import type { ConfigureSubnetRangesRequest } from '@nebula/shared/services'
import type { IPSubnet } from '@nebula/shared/types/network'

type SubnetRangeConfigProps = {
  subnet: IPSubnet
}

export default function SubnetRangeConfig({ subnet }: SubnetRangeConfigProps) {
  const queryClient = useQueryClient()
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [form, setForm] = useState<ConfigureSubnetRangesRequest>({
    dhcp_range_start: subnet.dhcp_range_start || '',
    dhcp_range_end: subnet.dhcp_range_end || '',
    static_range_start: subnet.static_range_start || '',
    static_range_end: subnet.static_range_end || '',
    dns_servers: subnet.dns_servers
      ? Array.isArray(subnet.dns_servers)
        ? subnet.dns_servers
        : subnet.dns_servers.split(',').map((s: string) => s.trim())
      : ['8.8.8.8', '8.8.4.4'],
  })

  const mutation = useMutation({
    mutationFn: (data: ConfigureSubnetRangesRequest) =>
      endCustomerIpamApi.configureRanges(subnet.id, data),
    onSuccess: () => {
      setSuccess(true)
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['ec-ipam-subnets'] })
      queryClient.invalidateQueries({ queryKey: ['ec-ipam-availability', subnet.id] })
      setTimeout(() => setSuccess(false), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Erro ao configurar ranges')
      setSuccess(false)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const payload: ConfigureSubnetRangesRequest = {
      dhcp_range_start: form.dhcp_range_start || null,
      dhcp_range_end: form.dhcp_range_end || null,
      static_range_start: form.static_range_start || null,
      static_range_end: form.static_range_end || null,
      dns_servers: form.dns_servers?.filter(Boolean) || null,
    }
    mutation.mutate(payload)
  }

  const handleDnsChange = (index: number, value: string) => {
    const newDns = [...(form.dns_servers || [])]
    newDns[index] = value
    setForm({ ...form, dns_servers: newDns })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Subnet Info */}
      <div className="rounded-lg bg-gray-50 p-4">
        <div className="mb-3 flex items-center space-x-2">
          <NetworkIcon className="size-5 text-blue-600" />
          <h4 className="font-medium text-gray-900">Subnet</h4>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <div>
            <span className="text-gray-500">CIDR</span>
            <p className="font-mono font-medium">{subnet.cidr}</p>
          </div>
          <div>
            <span className="text-gray-500">Gateway</span>
            <p className="font-mono font-medium">{subnet.gateway || '-'}</p>
          </div>
          <div>
            <span className="text-gray-500">Total IPs</span>
            <p className="font-medium">{subnet.total_ips}</p>
          </div>
          <div>
            <span className="text-gray-500">Alocados</span>
            <p className="font-medium">{subnet.allocated_ips}</p>
          </div>
        </div>
      </div>

      {/* DHCP Range */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <RadioIcon className="size-4 text-green-600" />
          <h4 className="text-sm font-medium text-gray-700">Faixa DHCP (automático)</h4>
        </div>
        <p className="text-xs text-gray-500">
          IPs nesta faixa serão entregues automaticamente via DHCP pelo gateway.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-600">IP Inicial</label>
            <input
              type="text"
              value={form.dhcp_range_start || ''}
              onChange={(e) => setForm({ ...form, dhcp_range_start: e.target.value })}
              placeholder="Ex: 10.0.0.100"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">IP Final</label>
            <input
              type="text"
              value={form.dhcp_range_end || ''}
              onChange={(e) => setForm({ ...form, dhcp_range_end: e.target.value })}
              placeholder="Ex: 10.0.0.200"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </div>

      {/* Static Range */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <ServerIcon className="size-4 text-blue-600" />
          <h4 className="text-sm font-medium text-gray-700">Faixa Estática (cloud-init)</h4>
        </div>
        <p className="text-xs text-gray-500">
          IPs nesta faixa serão configurados via cloud-init na criação da VM. Escolha "IP Estático"
          ao criar a VM.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-600">IP Inicial</label>
            <input
              type="text"
              value={form.static_range_start || ''}
              onChange={(e) => setForm({ ...form, static_range_start: e.target.value })}
              placeholder="Ex: 10.0.0.10"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">IP Final</label>
            <input
              type="text"
              value={form.static_range_end || ''}
              onChange={(e) => setForm({ ...form, static_range_end: e.target.value })}
              placeholder="Ex: 10.0.0.50"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* DNS Servers */}
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <InfoIcon className="size-4 text-gray-500" />
          <h4 className="text-sm font-medium text-gray-700">Servidores DNS</h4>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm text-gray-600">DNS Primário</label>
            <input
              type="text"
              value={form.dns_servers?.[0] || ''}
              onChange={(e) => handleDnsChange(0, e.target.value)}
              placeholder="8.8.8.8"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-gray-400"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">DNS Secundário</label>
            <input
              type="text"
              value={form.dns_servers?.[1] || ''}
              onChange={(e) => handleDnsChange(1, e.target.value)}
              placeholder="8.8.4.4"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm focus:border-transparent focus:ring-2 focus:ring-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center space-x-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
          <AlertTriangleIcon className="size-4 shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center space-x-2 rounded-lg border border-green-200 bg-green-50 p-3 text-green-700">
          <CheckCircleIcon className="size-4 shrink-0" />
          <span className="text-sm">Ranges configurados com sucesso!</span>
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="inline-flex items-center rounded-lg bg-green-600 px-4 py-2 text-white transition-colors hover:bg-green-700 disabled:opacity-50"
        >
          {mutation.isPending ? (
            <>
              <Loader2Icon className="mr-2 size-4 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <SaveIcon className="mr-2 size-4" />
              Salvar Configuracao
            </>
          )}
        </button>
      </div>
    </form>
  )
}
